/**
 * Google Analytics 4 Integration
 * Measurement ID: G-79M0MRKE52
 */

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// URL params to strip from tracking (internal tokens, etc.)
const INTERNAL_PARAMS = ['__lovable_token', '_gl', '_ga', 'fbclid', 'gclid'];

// Environment detection based on hostname
function getEnvironment(): 'prod' | 'staging' | 'dev' {
  if (typeof window === 'undefined') return 'dev';
  
  const hostname = window.location.hostname;
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'dev';
  }
  if (hostname.includes('staging') || hostname.includes('preview')) {
    return 'staging';
  }
  return 'prod';
}

/**
 * 🛠️ URL Normalizer - DRY (used by RouteTracker)
 * Fixes the duplicate page view issue (Enterprise vs enterprise)
 */
export function normalizeUrl(url: string): string {
  try {
    const parsed = new URL(url);
    // Remove internal params but keep campaign params (utm_*)
    INTERNAL_PARAMS.forEach(param => parsed.searchParams.delete(param));
    // Force lowercase and remove trailing slash
    parsed.pathname = parsed.pathname.toLowerCase().replace(/\/+$/, '') || '/';
    return parsed.toString();
  } catch {
    return url.toLowerCase();
  }
}

/**
 * Core tracking function - sends events to GA4
 * All events are enriched with env and timestamp
 */
export function trackGA(
  eventName: string, 
  params?: Record<string, any>
) {
  const env = getEnvironment();
  
  // 🆕 SKIP tracking in dev to prevent localhost pollution
  // Comment out this block if you want to test GA4 locally
  if (env === 'dev') {
    console.log(`[GA4] 🚫 DEV MODE - Skipped: ${eventName}`, params);
    return;
  }
  
  // Enrich params with standard fields
  const enrichedParams = {
    ...params,
    env,
    timestamp: Date.now(),
  };
  
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, enrichedParams);
  }
  
  // Always log for debugging (visible in console)
  console.log(`[GA4] 📊 ${eventName}:`, enrichedParams);
}

/**
 * Track AI generation performance
 * Accepts either startTime (for auto-calculation) or latencyMs directly
 */
export function trackAILatency(
  generationType: string,
  latencyMsOrStartTime: number,
  isSuccess: boolean,
  additionalParams?: Record<string, any>
) {
  // If value is large (> 1 billion), it's a timestamp - calculate latency
  const latencyMs = latencyMsOrStartTime > 1_000_000_000 
    ? Date.now() - latencyMsOrStartTime 
    : latencyMsOrStartTime;
  
  trackGA(GA_EVENTS.AI_GENERATION_COMPLETED, {
    generation_type: generationType,
    latency_ms: latencyMs,
    is_success: isSuccess,
    ...additionalParams,
  });
}

/**
 * Set user properties (called once per session)
 * Links anonymous auth user_id to GA4 for cross-session tracking
 */
export function setGAUser(userId: string, isAnonymous: boolean) {
  const env = getEnvironment();
  
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-79M0MRKE52', {
      user_id: userId,
    });
    window.gtag('set', 'user_properties', {
      is_anonymous: isAnonymous,
      env,
    });
  }
  console.log(`[GA4] 👤 User set:`, { userId, isAnonymous, env });
}

/**
 * Event constants for type safety and consistency
 * Use these instead of raw strings for event names
 */
export const GA_EVENTS = {
  // ========== Stage Transitions (Funnel) ==========
  FLOW_STARTED: 'flow_started',           // User enters /create OR clicks CTA
  BRIEF_SUBMITTED: 'brief_submitted',     // Phase 1 → 1.5 (send input_length_chars!)
  REVIEW_ENTERED: 'review_entered',       // Phase 1.5 render
  CAST_LOCATIONS_ENTERED: 'cast_locations_entered', // Phase 1.5 → 3
  STORYBOARD_ENTERED: 'storyboard_entered',   // Phase 3 → 4
  
  // ========== Conversion ==========
  EXPORT_CLICKED: 'export_clicked',       // 🆕 Intent - before process starts
  EXPORT_STARTED: 'export_started',       // Process actually started
  EXPORT_COMPLETED: 'export_completed',   // Export success
  EXPORT_FAILED: 'export_failed',         // Export error
  
  // ========== In-stage Actions ==========
  STYLE_SELECTED: 'style_selected',       // Visual style picker
  DURATION_SET: 'duration_set',           // Duration slider
  STORY_ARCHITECTURE_GENERATED: 'story_architecture_generated', // AI story gen
  SCRIPT_GENERATED: 'script_generated',   // AI script gen
  ENTITY_IMAGE_GENERATED: 'entity_image_generated', // Character/location/product image
  ENTITY_EDITED: 'entity_edited',         // Entity edit applied
  SCENE_IMAGE_GENERATED: 'scene_image_generated', // Storyboard scene image
  SCENE_EDITED: 'scene_edited',           // Scene edit applied
  SCENE_EDIT_OPENED: 'scene_edit_opened', // 🆕 Engagement - modal opened
  
  // ========== User Settings ==========
  VIDEO_TYPE_CHANGED: 'video_type_changed',
  VOICE_TYPE_CHANGED: 'voice_type_changed',
  LANGUAGE_CHANGED: 'language_changed',
  
  // ========== Flow Mode ==========
  FLOW_MODE_SELECTED: 'flow_mode_selected', // Generator vs Import
  
  // ========== Reference Images ==========
  REFERENCE_IMAGE_UPLOADED: 'reference_image_uploaded',
  REFERENCE_IMAGE_ANALYZED: 'reference_image_analyzed',
  REFERENCE_IMAGE_REMOVED: 'reference_image_removed',
  
  // ========== Document Import ==========
  DOCUMENT_UPLOADED: 'document_uploaded',
  DOCUMENT_PARSED: 'document_parsed',
  
  // ========== Story Examples ==========
  STORY_EXAMPLE_USED: 'story_example_used',

  // ========== Entity Actions ==========
  ENTITY_IMAGE_REGENERATED: 'entity_image_regenerated',
  ENTITY_IMAGE_EDITED: 'entity_image_edited',
  ENTITY_VERSION_CHANGED: 'entity_version_changed',
  ENTITY_MANUAL_OVERRIDE: 'entity_manual_override', // 🆕 Manual entity changes

  // ========== Scene Actions ==========
  SCENE_IMAGE_REGENERATED: 'scene_image_regenerated',
  SCENE_IMAGE_EDITED: 'scene_image_edited',

  // ========== Abandonment / Errors ==========
  PHASE_ABANDONED: 'phase_abandoned',
  GENERATION_ERROR: 'generation_error',
  
  // ========== CTA Tracking ==========
  CTA_CLICKED: 'cta_clicked',
  
  // ========== AI Performance ==========
  AI_GENERATION_STARTED: 'ai_generation_started', // 🆕 Track drop-off during wait
  AI_GENERATION_COMPLETED: 'ai_generation_completed',
} as const;

// Type for event names
export type GAEventName = typeof GA_EVENTS[keyof typeof GA_EVENTS];
